import React, { useState, useEffect } from "react";
import {
  Modal,
  Form,
  Input,
  Button,
  Space,
  Table,
  message,
  Row,
  Select,
} from "antd";
import { Formik, Form as FormikForm, Field } from "formik";
import * as Yup from "yup";
import axios from "axios";

const Address = () => {
  const [addresses, setAddresses] = useState([]);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingAddress, setEditingAddress] = useState(null);

  const fetchAddresses = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URI}/customer/address/list`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
          },
        }
      );
      setAddresses(response.data);
    } catch (error) {
      message.error("Failed to fetch addresses.");
    }
  };

  const handleSubmit = async (values, { resetForm }) => {
    const url = editingAddress
      ? `${process.env.REACT_APP_BACKEND_URI}/customer/address/update/${editingAddress.id}`
      : `${process.env.REACT_APP_BACKEND_URI}/customer/address/add`;
  
    const method = editingAddress ? "put" : "post";
  
    try {
      await axios({
        method,
        url,
        data: values,
        headers: {
          Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
        },
      });
      message.success(editingAddress ? "Address updated!" : "Address added!");
      fetchAddresses();
      resetForm();  // Reset the form fields
      setModalVisible(false);
      setEditingAddress(null);
    } catch (error) {
      message.error("Failed to save address.");
    }
  };
  

  const handleDelete = async (id) => {
    try {
      await axios.delete(
        `${process.env.REACT_APP_BACKEND_URI}/customer/address/delete`,
        {
          data: { address_id: id }, // Include the address_id in the data property
          headers: {
            Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
          },
        }
      );
      message.success("Address deleted!");
      fetchAddresses();
    } catch (error) {
      message.error("Failed to delete address.");
    }
  };

  useEffect(() => {
    fetchAddresses();
  }, []);

  const AddressSchema = Yup.object().shape({
    contact_person_name: Yup.string().required("Name is required"),
    address_type: Yup.string().required("Address type is required"),
    contact_person_number: Yup.string().required("Phone number is required"),
    address: Yup.string().required("Address is required"),
  });

  const columns = [
    {
      title: "Contact Person",
      dataIndex: "contact_person_name",
      key: "contact_person_name",
      sorter: (a, b) =>
        a.contact_person_name.localeCompare(b.contact_person_name),
    },
    {
      title: "Address Type",
      dataIndex: "address_type",
      key: "address_type",
    },
    {
      title: "Contact Number",
      dataIndex: "contact_person_number",
      key: "contact_person_number",
    },
    {
      title: "Address",
      dataIndex: "address",
      key: "address",
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <Space>
          <Button
            type="link"
            onClick={() => {
              setEditingAddress(record);
              setModalVisible(true);
            }}
            style={{ fontSize: "14px", fontWeight: "500", color: "#1890ff" }}
          >
            Edit
          </Button>
          <Button
            type="link"
            danger
            onClick={() => handleDelete(record.id)}
            style={{ fontSize: "14px", fontWeight: "500", color: "#ff4d4f" }}
          >
            Delete
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <div
      style={{
        padding: "24px",
        backgroundColor: "#f9f9f9",
        borderRadius: "8px",
      }}
    >
      <Row style={{ marginBottom: 16 }} justify="space-between">
        <h6 style={{ margin: 0 }}>Customer Address</h6>
        <Button
          className="btn-pri"
          type="primary"
          onClick={() => setModalVisible(true)}
          size="large"
        >
          Add Address
        </Button>
      </Row>

      <Table
        dataSource={addresses}
        columns={columns}
        rowKey="id"
        pagination={{ pageSize: 5 }}
        style={{ backgroundColor: "#fff", borderRadius: "8px" }}
        bordered
        rowClassName="table-row"
      />

      <Modal
        title={editingAddress ? "Edit Address" : "Add Address"}
        visible={modalVisible}
        onCancel={() => {
          setModalVisible(false);
          setEditingAddress(null);
        }}
        footer={null}
        style={{ borderRadius: "8px" }}
      >
        <Formik
        enableReinitialize
          initialValues={{
            contact_person_name: editingAddress?.contact_person_name || "",
            address_type: editingAddress?.address_type || "",
            contact_person_number: editingAddress?.contact_person_number || "",
            address: editingAddress?.address || "",
          }}
          validationSchema={AddressSchema}
          onSubmit={handleSubmit}
        >
          {({ errors, touched, handleSubmit }) => (
            <FormikForm>
              <Form.Item
                layout="vertical"
                label="Contact Person Name"
                validateStatus={
                  touched.contact_person_name && errors.contact_person_name
                    ? "error"
                    : ""
                }
                help={touched.contact_person_name && errors.contact_person_name}
              >
                <Field name="contact_person_name" as={Input} />
              </Form.Item>

              <Form.Item
                label="Address Type"
                layout="vertical"
                validateStatus={
                  touched.address_type && errors.address_type ? "error" : ""
                }
                help={touched.address_type && errors.address_type}
              >
                <Field name="address_type">
                  {({ field, form }) => (
                    <Select
                      {...field}
                      placeholder="Select Address Type"
                      style={{ width: "100%" }}
                      onChange={(value) =>
                        form.setFieldValue("address_type", value)
                      } // Explicitly setting value
                    >
                      <Select.Option value="Home">Home</Select.Option>
                      <Select.Option value="Work">Work</Select.Option>
                      <Select.Option value="Other">Other</Select.Option>
                    </Select>
                  )}
                </Field>
              </Form.Item>

              <Form.Item
                layout="vertical"
                label="Contact Number"
                validateStatus={
                  touched.contact_person_number && errors.contact_person_number
                    ? "error"
                    : ""
                }
                help={
                  touched.contact_person_number && errors.contact_person_number
                }
              >
                <Field name="contact_person_number" as={Input} />
              </Form.Item>

              <Form.Item
                layout="vertical"
                label="Address"
                validateStatus={
                  touched.address && errors.address ? "error" : ""
                }
                help={touched.address && errors.address}
              >
                <Field name="address" as={Input} />
              </Form.Item>

              <Form.Item>
                <Button
                  type="primary"
                  onClick={handleSubmit}
                  htmlType="submit"
                  size="large"
                  block
                >
                  {editingAddress ? "Update" : "Add"} Address
                </Button>
              </Form.Item>
            </FormikForm>
          )}
        </Formik>
      </Modal>
    </div>
  );
};

export default Address;
